import { memo, useEffect, useMemo, useRef, useState } from 'react'
import DocMarkdown from './DocMarkdown'
import { inlineReferenceImages } from './DocMarkdown'

interface Props {
  text: string
  className?: string
  /** Number of header-delimited sections rendered per page. When omitted
   *  we pick a content-aware default (see below): docs with inlined
   *  images get a much smaller page than text-only docs because each
   *  section carrying a base64 PNG can be hundreds of KB to several MB
   *  of markdown source. */
  sectionsPerPage?: number
  /** Optional key that scopes pagination memory. When the key stays the
   *  same and `text` changes (e.g. streaming) pagination is preserved.
   *  When the key changes the previous key's `pagesShown` is cached
   *  in-memory and the new key's prior value (or 1 if never visited) is
   *  restored. The cache lives for the lifetime of the component
   *  instance — unmounting clears it, which matches the desired scope:
   *  per-doc memory inside a docs panel, but not across tab/task switches.
   *  When omitted, pagination resets on every `text` change (legacy
   *  behaviour for non-docs consumers). */
  paginationKey?: string
  /** Optional request to reveal and scroll to an anchor that may live in a
   *  lazily hidden page. Increment `scrollTargetRequest` to repeat a request
   *  for the same id. */
  scrollTargetId?: string | null
  scrollTargetRequest?: number
}

// Page sizes for the content-aware default. Text-only docs can afford
// big pages — fewer load-mores, less sentinel churn. Image-heavy docs
// (mermaid diagrams, screenshots) get a tiny page so the initial parse
// + DOM creation + image-decode cost on first paint stays bounded; the
// IntersectionObserver auto-loads more as the user scrolls.
const TEXT_SECTIONS_PER_PAGE = 5
const IMAGE_SECTIONS_PER_PAGE = 1

/** Cheap detector for whether the (already-inlined) markdown contains
 *  data-URI images. We look for the `](data:image` substring rather
 *  than parsing because false negatives only mean we use the bigger
 *  page size — at worst the user pays the same cost they paid before
 *  this heuristic existed. */
function hasInlinedImages(text: string): boolean {
  return text.includes('](data:image')
}

/** Split markdown into top-level sections at H1 / H2 boundaries.
 *
 *  Strategy:
 *  - If the doc has 2+ H2 headers, split by H2 (finer granularity).
 *  - Else if the doc has 2+ H1 headers, split by H1.
 *  - Otherwise return a single section (the whole doc).
 *
 *  This keeps each section as a self-contained markdown blob that can be
 *  parsed independently — no orphan list items or split tables across page
 *  boundaries (since H1/H2 always start at the top level).
 */
export function splitMarkdownIntoSections(text: string): string[] {
  if (!text) return []
  const h2 = splitByPattern(text, /^## (?=\S)/gm)
  if (h2.length > 1) return h2
  const h1 = splitByPattern(text, /^# (?=\S)/gm)
  if (h1.length > 1) return h1
  return [text]
}

// `inlineReferenceImages` lives in `./DocMarkdown` (alongside the doc rules).
// Re-exported here for existing importers/tests.
export { inlineReferenceImages }

function splitByPattern(text: string, regex: RegExp): string[] {
  const indices: number[] = []
  let match: RegExpExecArray | null
  while ((match = regex.exec(text)) !== null) {
    indices.push(match.index)
    // Guard against zero-width matches, though our patterns can't produce them.
    if (regex.lastIndex === match.index) regex.lastIndex += 1
  }
  if (indices.length === 0) return [text]
  const sections: string[] = []
  // Capture any preamble before the first header (e.g. an intro paragraph).
  if (indices[0] > 0) {
    const preamble = text.slice(0, indices[0])
    if (preamble.trim()) sections.push(preamble)
  }
  for (let i = 0; i < indices.length; i++) {
    const start = indices[i]
    const end = i + 1 < indices.length ? indices[i + 1] : text.length
    sections.push(text.slice(start, end))
  }
  return sections
}

/** A single page's worth of markdown rendered through the standard
 *  DocMarkdown pipeline. Memoized on text so adding more pages doesn't
 *  cause already-rendered pages to re-parse. */
const PageMarkdown = memo(function PageMarkdown({ text }: { text: string }) {
  return <DocMarkdown text={text} />
})

/** Drop-in replacement for DocMarkdown that paginates large documents.
 *
 *  Renders the first `sectionsPerPage` sections immediately and lazily
 *  reveals subsequent pages as the user scrolls past a sentinel near the
 *  bottom. Each page is a separate memoized DocMarkdown instance, so
 *  revealing more pages never triggers a re-parse of already-rendered pages.
 *
 *  When the source `text` changes (e.g. user picks a different doc), the
 *  pagination resets to page 1 so the new doc starts at the top with a
 *  bounded initial render.
 */
const PaginatedDocMarkdown = memo(function PaginatedDocMarkdown({
  text,
  className,
  sectionsPerPage,
  paginationKey,
  scrollTargetId,
  scrollTargetRequest = 0
}: Props) {
  // Inline reference-style images BEFORE splitting so each page has the
  // image URLs literally embedded — see `inlineReferenceImages` for the
  // full rationale. Cheap memoised pass keyed on the same `text` dep
  // that drives section splitting.
  const inlinedText = useMemo(() => inlineReferenceImages(text), [text])
  const sections = useMemo(() => splitMarkdownIntoSections(inlinedText), [inlinedText])
  const sectionsPerPageByKeyRef = useRef<Map<string, number>>(new Map())

  // Content-aware default: image-heavy docs render one section per page so
  // initial paint stays bounded; text-only docs use the bigger page size
  // for fewer load-mores. During streaming, keep that decision stable per
  // `paginationKey` so a late image definition doesn't shrink already-rendered
  // pages and remount the visible document.
  // Explicit `sectionsPerPage` from a caller wins.
  const effectiveSectionsPerPage = useMemo(() => {
    if (sectionsPerPage !== undefined) return sectionsPerPage
    const detectedSectionsPerPage = hasInlinedImages(inlinedText)
      ? IMAGE_SECTIONS_PER_PAGE
      : TEXT_SECTIONS_PER_PAGE
    if (paginationKey === undefined) return detectedSectionsPerPage

    const cachedSectionsPerPage = sectionsPerPageByKeyRef.current.get(paginationKey)
    if (cachedSectionsPerPage !== undefined) return cachedSectionsPerPage

    sectionsPerPageByKeyRef.current.set(paginationKey, detectedSectionsPerPage)
    return detectedSectionsPerPage
  }, [sectionsPerPage, inlinedText, paginationKey])

  // In-memory per-key cache of how many pages were revealed for that key.
  // Lives for the component's lifetime; rebuilt fresh on remount, which
  // is exactly when the user would expect pagination to reset (entering
  // the docs panel, switching tasks, etc.).
  const pagesShownByKeyRef = useRef<Map<string, number>>(new Map())

  const [pagesShown, setPagesShown] = useState<number>(() => {
    if (paginationKey === undefined) return 1
    return pagesShownByKeyRef.current.get(paginationKey) ?? 1
  })
  const pendingScrollTargetRef = useRef<string | null>(null)

  // Detect a paginationKey transition and restore the new key's cached
  // value (or 1 for first visits). React's "adjusting state during render"
  // pattern: the setState calls below trigger an immediate re-render with
  // the corrected state before anything commits to the DOM.
  //
  // The cache write below is intentionally gated on the *stable* render
  // (i.e. NOT inside the transition branch). If we wrote on every render,
  // the discarded transition render would persist the stale `pagesShown`
  // from the *previous* key under the *new* key, polluting the cache.
  const [lastSyncedKey, setLastSyncedKey] = useState<string | undefined>(paginationKey)
  if (lastSyncedKey !== paginationKey) {
    setLastSyncedKey(paginationKey)
    setPagesShown(
      paginationKey !== undefined ? (pagesShownByKeyRef.current.get(paginationKey) ?? 1) : 1
    )
  } else if (paginationKey !== undefined) {
    pagesShownByKeyRef.current.set(paginationKey, pagesShown)
  }

  // Legacy behaviour: when no paginationKey is provided, reset pagination
  // every time the source text changes. Skip this when paginationKey is
  // present — the per-key cache handles "going back to a doc you were
  // already paged into."
  useEffect(() => {
    if (paginationKey !== undefined) return
    setPagesShown(1)
  }, [text, paginationKey])

  useEffect(() => {
    if (!scrollTargetId || scrollTargetRequest === 0) return

    const targetSectionIndex = sections.findIndex(section =>
      section.includes(`id="${scrollTargetId}"`)
    )
    if (targetSectionIndex < 0) return

    pendingScrollTargetRef.current = scrollTargetId
    const requiredPages = Math.ceil((targetSectionIndex + 1) / effectiveSectionsPerPage)
    setPagesShown(currentPages => Math.max(currentPages, requiredPages))
  }, [effectiveSectionsPerPage, scrollTargetId, scrollTargetRequest, sections])

  // Build each page's combined markdown source. Memoized so that going from
  // N pages to N+1 keeps the first N entries reference-equal, which lets
  // PageMarkdown skip re-rendering them.
  const pageContents = useMemo(() => {
    const pages: string[] = []
    const visibleSectionCount = Math.min(pagesShown * effectiveSectionsPerPage, sections.length)
    for (let start = 0; start < visibleSectionCount; start += effectiveSectionsPerPage) {
      const end = Math.min(start + effectiveSectionsPerPage, visibleSectionCount)
      pages.push(sections.slice(start, end).join(''))
    }
    return pages
  }, [sections, pagesShown, effectiveSectionsPerPage])

  const visibleSectionCount = Math.min(pagesShown * effectiveSectionsPerPage, sections.length)
  const remainingSections = sections.length - visibleSectionCount
  const hasMore = remainingSections > 0

  useEffect(() => {
    const targetId = pendingScrollTargetRef.current
    if (!targetId || typeof document === 'undefined') return

    const target = document.getElementById(targetId)
    if (!target) return

    pendingScrollTargetRef.current = null
    const scrollToTarget = () => target.scrollIntoView({ block: 'start', behavior: 'smooth' })
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(scrollToTarget)
    } else {
      scrollToTarget()
    }
  }, [pageContents, scrollTargetId, scrollTargetRequest])

  // Auto-load next page when the sentinel scrolls into the nearest scroll
  // container. `rootMargin` pre-fetches a bit before the user actually hits
  // bottom so the next chunk is mounted by the time they get there.
  const sentinelRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!hasMore) return
    const node = sentinelRef.current
    if (!node) return
    if (typeof IntersectionObserver === 'undefined') {
      // SSR / very old browsers — fall back to revealing everything at once
      // so content stays reachable.
      setPagesShown(p => Math.max(p, Math.ceil(sections.length / effectiveSectionsPerPage)))
      return
    }
    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          setPagesShown(p => p + 1)
        }
      },
      { rootMargin: '400px 0px' }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [hasMore, sections.length, effectiveSectionsPerPage])

  // Single-section docs (or empty text) — render directly without any
  // pagination chrome so the rendered output is identical to plain
  // DocMarkdown for short content.
  if (sections.length <= 1) {
    // Use the inlined text here too: when the doc is short enough to
    // skip pagination, the original `text` would render fine on its own
    // (refs and defs in the same parse pass), but inlining keeps the
    // two render paths consistent and is cheap when there's nothing to
    // inline.
    return <DocMarkdown text={inlinedText} className={className} />
  }

  return (
    <div className={className} data-testid='paginated-markdown'>
      {pageContents.map((content, i) => (
        <PageMarkdown key={i} text={content} />
      ))}
      {hasMore && (
        <div
          ref={sentinelRef}
          data-testid='paginated-markdown-sentinel'
          aria-hidden='true'
          className='h-1 w-full'
        />
      )}
    </div>
  )
})

export default PaginatedDocMarkdown
